import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showModal } from '../../../../redux/system/actions';
import * as Yup from 'yup';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import CreateConsultancyExperienceModal from '../../../common/modals/CreateConsultancyExperienceModal';
import ConfirmModal from '../../../common/modals/ConfirmModal';

const CREATE_TS_VIEW_CONSULTANCY_MODAL_ID = 'createTSViewConsultancy';
const CANCEL_MODAL_ID = 'cancelModal'

const validationSchema = Yup.object().shape({
  jobTitle: Yup.string().trim().required('Job title is required'),
  employer: Yup.string().trim().required('Employer is required'),
  town: Yup.string().trim().required('Town is required'),
  startMonth: Yup.number().required('Start month is required').min(1, 'Must be between 1 and 12').max(12, 'Must be between 1 and 12'),
  startYear: Yup.number()
    .required('Start year is required')
    .test('not-in-future', 'Start date cannot be in the future', function (startYear) {
      const { startMonth } = this.parent;
      if (!startYear || !startMonth) return true;

      const selectedDate = new Date(startYear, startMonth - 1);
      const today = new Date();
      selectedDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      return selectedDate <= today;
    }),
  currentJob: Yup.boolean(),
  endMonth: Yup.number().when('currentJob', {
    is: false,
    then: (schema) => schema.required('End month is required'),
    otherwise: (schema) => schema,
  }),
  endYear: Yup.number().when('currentJob', {
    is: false,
    then: (schema) => schema.required('End year is required'),
    otherwise: (schema) => schema,
  }),
  isConsultancy: Yup.boolean(),
  consultancyCompany: Yup.string().nullable(),
  description: Yup.string().nullable(),
});

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const years = Array.from({ length: 50 }, (_, i) => ({
  value: new Date().getFullYear() - i,
  label: new Date().getFullYear() - i,
}));

function ExperienceForm({ curriculumVitaeId, initialExperience, setEditingExperience, setAddingExperience, onSave, status }) {
  const dispatch = useDispatch();
  const [isChecked, setIsChecked] = useState(false);

  const [experience, setExperience] = useState(
    initialExperience || {
      jobTitle: '',
      employer: '',
      town: '',
      startMonth: '',
      startYear: '',
      startDate: '',
      endMonth: '',
      endYear: '',
      endDate: '',
      currentJob: false,
      isConsultancy: false,
      consultancyCompany: '',
      description: '',
      curriculumVitaeId,
    },
  );

  const clearForm = () => {
    setEditingExperience(null);

    setExperience({
      jobTitle: '',
      employer: '',
      town: '',
      startMonth: '',
      startYear: '',
      startDate: '',
      endMonth: '',
      endYear: '',
      endDate: '',
      currentJob: false,
      isConsultancy: false,
      consultancyCompany: '',
      description: '',
      curriculumVitaeId,
    });
  };

  const handleShowModal = useCallback((modalId) => dispatch(showModal(modalId)), [dispatch]);

  const formatDate = (year, month) => {
    if (!year || !month) return null;
    const date = new Date(year, month);
    return date.toISOString().split('T')[0];
  };

  const handleChange = (e, formValues) => {
    const checked = e.target.checked;

    if (!checked) {
      setExperience((prev) => ({
        ...prev,
        isConsultancy: false,
        consultancyCompany: '',
      }));
    } else {
      if (formValues) {
        setExperience((prev) => ({
          ...prev,
          ...formValues,
        }));
      }
      handleShowModal(CREATE_TS_VIEW_CONSULTANCY_MODAL_ID);
    }
  };

  const handleCancel = () => {
    handleShowModal(CANCEL_MODAL_ID);
  };

  const confirmCancel = () => {
    setAddingExperience(false);
    setEditingExperience(null);
  };

  useEffect(() => {
    if (initialExperience) {
      const startDate = initialExperience.startDate ? new Date(initialExperience.startDate) : null;
      const endDate = initialExperience.endDate ? new Date(initialExperience.endDate) : null;

      const startMonth = startDate ? startDate.getMonth() + 1 : '';
      const startYear = startDate ? startDate.getFullYear() : '';
      const endMonth = endDate ? endDate.getMonth() + 1 : '';
      const endYear = endDate ? endDate.getFullYear() : '';

      setExperience({
        ...initialExperience,
        startMonth: startMonth,
        startYear: startYear,
        endMonth: endMonth,
        endYear: endYear,
      });

    }
  }, [initialExperience]);

  return (
    <div>
      <Formik
        enableReinitialize={true}
        initialValues={experience}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          const updatedExperience = {
            ...values,
            startDate: formatDate(values.startYear, values.startMonth),
            endDate: values.currentJob ? null : formatDate(values.endYear, values.endMonth),
            isConsultancy: experience.isConsultancy,
            consultancyCompany: experience.consultancyCompany,
          };

          // TODO: TEMP FIX (we need to validate this in our schema)
          if (updatedExperience.endDate) {
            const start = new Date(updatedExperience.startDate);
            const end = new Date(updatedExperience.endDate);

            if (end < start) {
              alert('End date cannot be earlier than start date');
              return;
            }
          }

          onSave(updatedExperience);
          clearForm();
          resetForm();
        }}>
        {({ values }) => (
          <Form>
            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <label className="small">JOB TITLE *</label>
                  <Field name="jobTitle">
                    {({ field, meta }) => (
                      <input
                        {...field}
                        type="text"
                        className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                        placeholder="Enter job title"

                      />
                    )}
                  </Field>
                  <ErrorMessage name="jobTitle">
                    {(msg) => (
                      <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label className="small">EMPLOYER *</label>
                  <Field name="employer">
                    {({ field, meta }) => (
                      <input
                        {...field}
                        type="text"
                        className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                        placeholder="Enter employer"

                      />
                    )}
                  </Field>
                  <ErrorMessage name="employer">
                    {(msg) => (
                      <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label className="small">TOWN *</label>
                  <Field name="town">
                    {({ field, meta }) => (
                      <input
                        {...field}
                        type="text"
                        className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                        placeholder="Enter town"

                      />
                    )}
                  </Field>
                  <ErrorMessage name="town">
                    {(msg) => (
                      <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label className="small">START DATE *</label>
                  <div className="d-flex">
                    <div className="mr-2" style={{ flex: '1' }}>
                      <Field name="startMonth">
                        {({ field, meta }) => (
                          <select
                            {...field}
                            className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                          >
                            <option value="">Month</option>
                            {months.map((month) => (
                              <option key={month.value} value={month.value}>
                                {month.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                      <ErrorMessage name="startMonth">
                        {(msg) => (
                          <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                            {msg}
                          </div>
                        )}
                      </ErrorMessage>
                    </div>

                    <div style={{ flex: '1' }}>
                      <Field name="startYear">
                        {({ field, meta }) => (
                          <select
                            {...field}
                            className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                          >
                            <option value="">Year</option>
                            {years.map((year) => (
                              <option key={year.value} value={year.value}>
                                {year.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                      <ErrorMessage name="startYear">
                        {(msg) => (
                          <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                            {msg}
                          </div>
                        )}
                      </ErrorMessage>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label className="small">END DATE *</label>
                  <div className="d-flex">
                    <div className="mr-2" style={{ flex: '1' }}>
                      <Field name="endMonth">
                        {({ field, meta }) => (
                          <select
                            {...field}
                            disabled={values.currentJob}
                            className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                          >
                            <option value="">Month</option>
                            {months.map((month) => (
                              <option key={month.value} value={month.value}>
                                {month.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                      <ErrorMessage name="endMonth">
                        {(msg) => (
                          <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                            {msg}
                          </div>
                        )}
                      </ErrorMessage>
                    </div>

                    <div style={{ flex: '1' }}>
                      <Field name="endYear">
                        {({ field, meta }) => (
                          <select
                            {...field}
                            disabled={values.currentJob}
                            className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
                          >
                            <option value="">Year</option>
                            {years.map((year) => (
                              <option key={year.value} value={year.value}>
                                {year.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                      <ErrorMessage name="endYear">
                        {(msg) => (
                          <div className="text-danger mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                            {msg}
                          </div>
                        )}
                      </ErrorMessage>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4 d-flex align-items-center">
                <div>
                  <div className="form-group mb-2 d-flex align-items-center">
                    <Field
                      type="checkbox"
                      name="currentJob"
                      className="form-check-input m-0"
                    />
                    <label className="small ml-4 mt-0 mb-0">Current Job</label>
                  </div>

                  <div className="form-group mb-0 d-flex align-items-center">
                    <Field name="isConsultancy">
                      {({ field }) => (
                        <input
                          {...field}
                          type="checkbox"
                          checked={field.value}
                          className="form-check-input m-0"
                          onChange={(e) => {
                            field.onChange(e);
                            handleChange(e, values);
                          }}
                        />
                      )}
                    </Field>
                    <label className="small ml-4 mt-0 mb-0">Is consultancy</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="form-group">
                  <label htmlFor="description" className="small">
                    DESCRIPTION
                  </label>
                  <Field
                    as="textarea"
                    name="description"
                    className="form-control"
                    rows={4}
                    placeholder="Enter description"
                  />
                </div>
              </div>
            </div>

            <div className="mb-5">
              {initialExperience ? (
                <div className="d-flex align-items-start">
                  <button className="btn btn-primary d-flex align-items-center mt-2 mr-3" type="submit">
                    <i className="pe-7s-pen mr-2"></i>
                    <span>Update Experience</span>
                  </button>
                  {status === 1 ? (
                    <button className="cancel-button d-flex mt-2" type="button" onClick={handleCancel}>
                      <span>Cancel</span>
                    </button>
                  ) : (
                    <button className="cancel-button d-flex align-items-center mt-2" type="button" onClick={clearForm}>
                      <span>Cancel</span>
                    </button>
                  )}
                </div>
              ) : (
                <div className='d-flex'>
                  <button className="btn btn-primary d-flex mt-2 mr-3" type="submit">
                    <i className="pe-7s-plus mr-2"></i>
                    <span>Add Experience</span>
                  </button>
                  {status === 1 && (
                    <button className="cancel-button d-flex mt-2" type="button" onClick={handleCancel}>
                      <span>Cancel</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </Form>
        )}
      </Formik>
      <ConfirmModal
        id={CANCEL_MODAL_ID}
        title="Are you sure you want to stop adding this professional experience?"
        message="This will cancel the form and return you to the overview. Do you wish to proceed?"
        onConfirm={confirmCancel}
      />
      <CreateConsultancyExperienceModal id={CREATE_TS_VIEW_CONSULTANCY_MODAL_ID} experience={experience} setExperience={setExperience} setIsChecked={setIsChecked} />
    </div>
  );
}

export default ExperienceForm;
